import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AgentChatState,
  AgentConversationContext,
  AgentStatus,
} from "../types";
import { ConversationMessage } from "@stratify/shared";

const initialState: AgentChatState = {
  context: null,
  messages: [],
  status: "idle",
  isSending: false,
  error: null,
};

const agentChatSlice = createSlice({
  name: "agentChat",
  initialState,
  reducers: {
    // Set the current conversation context (path prefix and ID)
    setConversationContext(
      state,
      action: PayloadAction<AgentConversationContext>
    ) {
      state.context = action.payload;
      state.messages = []; // Clear messages when switching conversations
      state.status = "idle";
      state.error = null;
    },

    // Message sending actions
    sendMessageStart(state, _action: PayloadAction<{ text: string }>) {
      state.isSending = true;
      state.error = null;
    },
    sendMessageSuccess(state) {
      state.isSending = false;
      state.error = null;
    },
    sendMessageFailure(state, action: PayloadAction<string>) {
      state.isSending = false;
      state.error = action.payload;
    },

    // Update messages from Firestore subscription
    messagesReceived(state, action: PayloadAction<ConversationMessage[]>) {
      state.messages = action.payload;

      // Infer status from the latest message
      const latestMessage = action.payload[action.payload.length - 1];
      if (latestMessage) {
        switch (latestMessage.type) {
          case "tool_call":
            state.status = "working";
            break;
          case "agent":
            // If the agent message is being streamed (has tokens but no text),
            // it's still thinking. Otherwise, it's done.
            const content = latestMessage.content;
            if (content.tokens && content.tokens.length > 0 && !content.text) {
              state.status = "thinking";
            } else {
              state.status = "idle";
            }
            break;
          case "error":
            state.status = "error";
            state.error = latestMessage.content.message;
            break;
        }
      }
    },

    // Explicitly set status if needed
    setStatus(state, action: PayloadAction<AgentStatus>) {
      state.status = action.payload;
    },

    // Clear error state
    clearError(state) {
      state.error = null;
    },

    // Reset conversation state
    clearConversation(state) {
      state.context = null;
      state.messages = [];
      state.status = "idle";
      state.isSending = false;
      state.error = null;
    },
  },
});

export const {
  setConversationContext,
  sendMessageStart,
  sendMessageSuccess,
  sendMessageFailure,
  messagesReceived,
  setStatus,
  clearError,
  clearConversation,
} = agentChatSlice.actions;

export default agentChatSlice.reducer;
